import React, { useState, useRef } from 'react'
import { 
  Card, 
  Button, 
  Space, 
  Upload, 
  message, 
  Progress, 
  List,
  Typography,
  Divider,
  Alert,
  Spin
} from 'antd'
import { 
  UploadOutlined, 
  FileTextOutlined, 
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL, apiRequest } from '../../utils/api'

const { Text, Title } = Typography

function BatchImport() {
  const navigate = useNavigate()
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadResults, setUploadResults] = useState([])
  const [isProcessing, setIsProcessing] = useState(false)
  const uploadRef = useRef(null)
  
  const handleFileChange = ({ fileList }) => {
    setFiles(fileList)
  }
  
  const handleUpload = async () => {
    if (files.length === 0) {
      message.warning('请先选择要上传的文件')
      return
    }
    
    setUploading(true)
    setProgress(0)
    setUploadResults([])
    
    try {
      // 创建FormData对象
      const formData = new FormData()
      
      // 添加所有文件到FormData
      files.forEach((fileObj, index) => {
        formData.append(`files`, fileObj.originFileObj)
      })
      
      // 发送上传请求
      // 在生产环境中使用完整的URL，在开发环境中使用相对路径
      const url = process.env.NODE_ENV === 'development' 
        ? '/api/admin/books/batch-import'
        : `${API_BASE_URL}/admin/books/batch-import`
      
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      })
      
      if (response.ok) {
        const data = await response.json()
        message.success('文件上传成功，开始处理...')
        
        // 开始轮询处理进度
        pollProcessingProgress(data.taskId)
      } else {
        const errorData = await response.json()
        message.error(errorData.error || '文件上传失败')
        setUploading(false)
      }
    } catch (error) {
      console.error('上传文件失败:', error)
      message.error('上传文件失败')
      setUploading(false)
    }
  }
  
  const pollProcessingProgress = (taskId) => {
    setIsProcessing(true)
    setProgress(0)
    
    const interval = setInterval(async () => {
      try {
        const response = await apiRequest(`/admin/books/import-progress/${taskId}`)
        const data = await response.json()
        
        if (response.ok) {
          setProgress(data.progress || 0)
          setUploadResults(data.results || [])
          
          if (data.status === 'completed') {
            clearInterval(interval)
            setIsProcessing(false)
            setUploading(false)
            message.success('批量导入完成')
          } else if (data.status === 'error') {
            clearInterval(interval)
            setIsProcessing(false)
            setUploading(false)
            message.error('批量导入出错')
          }
        }
      } catch (error) {
        console.error('获取处理进度失败:', error)
        clearInterval(interval)
        setIsProcessing(false)
        setUploading(false)
      }
    }, 2000)
  }
  
  const beforeUpload = (file) => {
    const isTxt = file.type === 'text/plain' || file.name.endsWith('.txt')
    if (!isTxt) {
      message.error('只能上传txt格式的文件!')
      return false
    }
    
    const isLt50M = file.size / 1024 / 1024 < 50
    if (!isLt50M) {
      message.error('文件大小不能超过50MB!')
      return false
    }
    
    return true
  }
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      default:
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />
    }
  }
  
  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return '成功'
      case 'error':
        return '失败'
      default:
        return '处理中'
    }
  }
  
  return (
    <div className="batch-import">
      <Card className="management-card">
        <div className="import-header">
          <Space size="middle">
            <UploadOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <Title level={3} style={{ margin: 0 }}>批量导入书籍</Title>
              <Text type="secondary">支持上传多个txt格式的书籍文件</Text>
            </div>
          </Space>
        </div>
        
        <Divider />
        
        <Alert
          message="导入说明"
          description={
            <div>
              <p>1. 支持上传.txt格式的文本文件</p>
              <p>2. 单个文件大小不能超过50MB</p>
              <p>3. 系统会自动解析文件内容并提取章节信息</p>
              <p>4. 导入完成后可在书籍管理中查看和编辑书籍信息</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        
        {/* 文件上传区域 */}
        <div style={{ marginBottom: 24 }}>
          <Upload
            ref={uploadRef}
            fileList={files}
            onChange={handleFileChange}
            beforeUpload={beforeUpload}
            multiple
            showUploadList
            accept=".txt"
          >
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
        </div>
        
        {/* 上传按钮 */}
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button
              type="primary"
              onClick={handleUpload}
              loading={uploading}
              disabled={files.length === 0 || uploading}
            >
              开始导入
            </Button>
            <Button
              onClick={() => {
                setFiles([])
                setUploadResults([])
              }}
              disabled={files.length === 0 || uploading}
            >
              清空列表
            </Button>
          </Space>
        </div>
        
        {/* 进度条 */}
        {(uploading || isProcessing) && (
          <div style={{ marginBottom: 24 }}>
            <Progress 
              percent={progress} 
              status={isProcessing ? 'active' : 'normal'} 
              format={(percent) => `${percent}%`}
            />
            <Text type="secondary">
              {uploading ? '文件上传中...' : '文件处理中...'}
            </Text>
          </div>
        )}
        
        {/* 处理结果 */}
        {uploadResults.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>处理结果</Title>
            <List
              dataSource={uploadResults}
              renderItem={result => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<FileTextOutlined />}
                    title={result.filename}
                    description={
                      <Space>
                        {getStatusIcon(result.status)}
                        <span>{getStatusText(result.status)}</span>
                        {result.message && (
                          <Text type={result.status === 'error' ? 'danger' : 'secondary'}>
                            {result.message}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}
        
        {/* 操作提示 */}
        <Card size="small">
          <Space>
            <Button 
              type="link" 
              onClick={() => navigate('/admin/books')}
            >
              查看书籍列表
            </Button>
            <Button 
              type="link" 
              onClick={() => navigate('/admin/pages')}
            >
              生成页面
            </Button>
          </Space>
        </Card>
      </Card>
    </div>
  )
}

export default BatchImport